import { useMemo } from "react";
import { AvatarGroup, AvatarItem } from "@tellescope/ui";
import { formatInitials, lastDigitsOfPhone } from "@tellescope/utils";
import type { Participant } from "../useCallWindow";

export interface ParticipantAvatarsProps {
  /** List of participants to display */
  participants: Participant[];
  /** Maximum number of avatars to show before showing "+N" */
  maxVisible?: number;
  /** Size of each avatar */
  size?: "sm" | "md" | "lg";
  /** Additional className for the container */
  className?: string;
  /** Show loading state */
  isLoading?: boolean;
}

/**
 * ParticipantAvatars - Displays avatars for call participants.
 *
 * Wraps the AvatarGroup component with call-specific logic for
 * generating initials and handling different participant types.
 *
 * @example
 * ```tsx
 * <ParticipantAvatars
 *   participants={[
 *     { id: "1", name: "John Doe", type: "user" },
 *     { id: "2", name: "+1 555-123-4567", type: "phone" }
 *   ]}
 *   maxVisible={4}
 *   size="md"
 * />
 * ```
 */
export function ParticipantAvatars({
  participants,
  maxVisible = 4,
  size = "md",
  className,
  isLoading = false,
}: ParticipantAvatarsProps) {
  const items: AvatarItem[] = useMemo(() => {
    return participants.map((p) => ({
      id: p.id,
      name: p.name,
      avatar: p.avatar,
      initials: p.type === "phone" ? lastDigitsOfPhone(p.name) || "?" : formatInitials(p.name).toUpperCase() || "?",
    }));
  }, [participants]);

  return (
    <AvatarGroup
      items={items}
      maxVisible={maxVisible}
      size={size}
      className={className}
      isLoading={isLoading}
      emptyFallback="?"
    />
  );
}
